import { ExtractedGstInvoice, ItcEligibility } from '../types/gst';

export interface GstLawRule {
  id: string;
  section: string;
  act: string;
  category: 'ELIGIBLE' | 'BLOCKED_17_5' | 'RCM_APPLICABLE' | 'INELIGIBLE';
  title: string;
  description: string;
  founderAdvice: string;
  keywords: string[];
  sacHsnPrefixes: string[];
}

export const GST_LAW_KNOWLEDGE_BASE: GstLawRule[] = [
  // SECTION 17(5) BLOCKED CREDITS
  {
    id: "SEC_17_5_A_VEHICLES",
    section: "Section 17(5)(a)",
    act: "CGST Act, 2017",
    category: "BLOCKED_17_5",
    title: "Motor Vehicles for Passenger Transportation",
    description: "Input Tax Credit is strictly blocked on motor vehicles for transportation of persons having approved seating capacity of not more than 13 persons (including the driver), along with their servicing, repair, insurance, and maintenance.",
    founderAdvice: "ITC on company car purchases, car insurance, petrol/diesel, Uber/Ola cab rides, and car maintenance is blocked under Sec 17(5)(a). It must not be claimed in GSTR-3B Table 4(A).",
    keywords: ["motor vehicle", "car", "suv", "cab", "uber", "ola", "car rental", "vehicle lease", "car repair", "car insurance", "petrol", "diesel", "fuel"],
    sacHsnPrefixes: ["8702", "8703", "9964", "9971", "9987"]
  },
  {
    id: "SEC_17_5_B_FOOD_BEVERAGES",
    section: "Section 17(5)(b)(i)",
    act: "CGST Act, 2017",
    category: "BLOCKED_17_5",
    title: "Food, Beverages & Outdoor Catering",
    description: "ITC is blocked on food and beverages, outdoor catering, beauty treatment, health services, cosmetic and plastic surgery, unless used in the same line of business or mandated by statutory law for employees.",
    founderAdvice: "Team lunches, client dinners, Swiggy, Zomato, Starbucks, snacks, and catering receipts are blocked under Sec 17(5)(b)(i). Do not claim ITC on office refreshments.",
    keywords: ["food", "beverage", "restaurant", "catering", "lunch", "dinner", "swiggy", "zomato", "starbucks", "coffee", "tea", "snacks", "meal", "bar", "liquor", "cafe"],
    sacHsnPrefixes: ["9963"]
  },
  {
    id: "SEC_17_5_B_MEMBERSHIP",
    section: "Section 17(5)(b)(ii)",
    act: "CGST Act, 2017",
    category: "BLOCKED_17_5",
    title: "Club, Health & Fitness Centre Memberships",
    description: "Input Tax Credit is blocked on membership fees of a club, health, and fitness centre.",
    founderAdvice: "Gym memberships, sports clubs, country clubs, or recreational club subscriptions for directors or employees cannot be claimed as ITC.",
    keywords: ["gym", "fitness", "club membership", "country club", "cult.fit", "sports membership", "health club"],
    sacHsnPrefixes: ["9997", "9996"]
  },
  {
    id: "SEC_17_5_C_WORKS_CONTRACT",
    section: "Section 17(5)(c) & (d)",
    act: "CGST Act, 2017",
    category: "BLOCKED_17_5",
    title: "Works Contract & Construction of Immovable Property",
    description: "ITC is blocked on works contract services and goods/services used for construction of an immovable property (other than plant & machinery) on own account, even when used in furtherance of business.",
    founderAdvice: "Office building civil construction, structural alterations, flooring, tiling, and permanent architectural works are blocked. ITC is only allowed on movable plant & machinery or IT networking equipment.",
    keywords: ["works contract", "civil construction", "building construction", "office construction", "renovation of immovable property", "flooring", "tiling", "masonry", "structural"],
    sacHsnPrefixes: ["9954"]
  },
  {
    id: "SEC_17_5_G_PERSONAL",
    section: "Section 17(5)(g) & (h)",
    act: "CGST Act, 2017",
    category: "BLOCKED_17_5",
    title: "Personal Consumption, Gifts & Lost Goods",
    description: "ITC is blocked on goods or services used for personal consumption, or goods lost, stolen, destroyed, written off, or disposed of by way of gift or free samples.",
    founderAdvice: "Personal expenses charged to company cards or festive Diwali gifts distributed to employees/clients are blocked under Sec 17(5)(h).",
    keywords: ["personal consumption", "personal expense", "gift", "free sample", "written off", "diwali gift", "corporate gift"],
    sacHsnPrefixes: []
  },

  // REVERSE CHARGE MECHANISM (RCM)
  {
    id: "SEC_9_3_IMPORT_SERVICES",
    section: "Section 9(3) / Section 5(3) IGST Act",
    act: "IGST Act, 2017 (Notif. 10/2017-IT)",
    category: "RCM_APPLICABLE",
    title: "Import of Services (Foreign SaaS, Cloud & AI Subscriptions)",
    description: "Import of services supplied by any person located in a non-taxable territory to any person located in the taxable territory other than non-taxable online recipient (OIDAR) attracts 100% Reverse Charge Mechanism.",
    founderAdvice: "Foreign cloud invoices (AWS, OpenAI, GitHub, Google Cloud, Vercel, Stripe, Notion, Figma, Anthropic) billed without Indian GSTIN are subject to RCM. You must pay 18% IGST in cash in GSTR-3B Table 3.1(d) and claim 100% ITC in Table 4(A)(2) in the same return.",
    keywords: ["aws", "amazon web services", "openai", "chatgpt", "github", "google cloud", "vercel", "supabase", "stripe", "notion", "figma", "anthropic", "slack", "zoom", "foreign saas", "import of service"],
    sacHsnPrefixes: ["9983", "9984"]
  },
  {
    id: "SEC_9_3_LEGAL_SERVICES",
    section: "Section 9(3) CGST Act",
    act: "CGST Act, 2017 (Notif. 13/2017-CT)",
    category: "RCM_APPLICABLE",
    title: "Legal Services by Advocates / Law Firms",
    description: "Services supplied by an individual advocate including a senior advocate or firm of advocates by way of legal services directly or indirectly to any business entity located in the taxable territory.",
    founderAdvice: "Invoices from advocates, legal counsels, and law firms are subject to RCM. The recipient company must pay the GST under RCM and claim ITC.",
    keywords: ["advocate", "legal service", "law firm", "solicitor", "barrister", "legal counsel", "litigation"],
    sacHsnPrefixes: ["9982"]
  },
  {
    id: "SEC_9_3_DIRECTOR_SERVICES",
    section: "Section 9(3) CGST Act",
    act: "CGST Act, 2017 (Notif. 13/2017-CT)",
    category: "RCM_APPLICABLE",
    title: "Services Supplied by a Director to the Company",
    description: "Services supplied by a director of a company or a body corporate to the said company or the body corporate (sitting fees, consulting, guarantees).",
    founderAdvice: "Director sitting fees and independent director consulting are subject to RCM under Section 9(3). Recipient company must discharge GST under RCM.",
    keywords: ["director fee", "director sitting fee", "director remuneration", "director commission"],
    sacHsnPrefixes: ["9983"]
  },
  {
    id: "SEC_9_3_GTA_SERVICES",
    section: "Section 9(3) CGST Act",
    act: "CGST Act, 2017 (Notif. 13/2017-CT)",
    category: "RCM_APPLICABLE",
    title: "Goods Transport Agency (GTA) Services",
    description: "Supply of services by a Goods Transport Agency (GTA) in respect of transportation of goods by road to a registered person (unless GTA opted for forward charge).",
    founderAdvice: "Freight charges paid to road transporters / GTA require 5% GST payment under RCM if the transporter did not charge 12% forward charge on consignment note.",
    keywords: ["gta", "goods transport agency", "freight", "road transport of goods", "consignment note"],
    sacHsnPrefixes: ["9965", "9967"]
  },

  // SECTION 16 ELIGIBLE CREDITS
  {
    id: "SEC_16_ELIGIBLE_ITC",
    section: "Section 16(2)",
    act: "CGST Act, 2017",
    category: "ELIGIBLE",
    title: "Eligible B2B Input Tax Credit",
    description: "Registered person is entitled to take credit of input tax charged on any supply of goods or services used or intended to be used in the course or furtherance of business, provided tax invoice is possessed, goods/services received, tax reflected in GSTR-2B, and paid within 180 days.",
    founderAdvice: "Fully eligible business expenditure (software development, cloud servers with Indian GSTIN, office laptops, marketing agency, professional fees). Ensure vendor payment within 180 days.",
    keywords: ["saas", "software", "consulting", "laptop", "server", "agency", "marketing", "office rent", "audit fee"],
    sacHsnPrefixes: ["9983", "9972", "8471"]
  }
];

/**
 * Searches the Indian GST Law Knowledge Base
 */
export function searchGstKnowledgeBase(query: string): GstLawRule[] {
  if (!query) return GST_LAW_KNOWLEDGE_BASE;
  const q = query.toLowerCase().trim();

  return GST_LAW_KNOWLEDGE_BASE.filter(rule => {
    return (
      rule.section.toLowerCase().includes(q) ||
      rule.title.toLowerCase().includes(q) ||
      rule.description.toLowerCase().includes(q) ||
      rule.keywords.some(k => q.includes(k) || k.includes(q)) ||
      rule.sacHsnPrefixes.some(p => q.includes(p))
    );
  });
}

/**
 * Evaluates an invoice against Indian GST law and determines ITC eligibility and RCM applicability
 */
export function classifyInvoiceCompliance(invoice: Partial<ExtractedGstInvoice>): {
  itcEligibility: ItcEligibility;
  aiComplianceNote: string;
  reverseChargeApplicable: boolean;
  matchedRule?: GstLawRule;
} {
  const supplierGstin = (invoice.supplier_gstin || "").trim().toUpperCase();
  const supplierName = (invoice.supplier_name || "").toLowerCase();
  const category = invoice.category;

  // 1. Check for Foreign SaaS / Import of Services (RCM)
  const foreignSaaSKeywords = [
    "openai", "chatgpt", "aws", "amazon web services", "github", 
    "google cloud", "vercel", "supabase", "stripe", "notion", 
    "figma", "anthropic", "slack", "zoom video", "digitalocean", "cloudflare"
  ];

  const isForeignVendor = foreignSaaSKeywords.some(v => supplierName.includes(v));
  const hasNoIndianGstin = !supplierGstin || supplierGstin.length !== 15 || supplierGstin.startsWith("99");

  if ((isForeignVendor && hasNoIndianGstin) || (category === 'EXPENSE_RECEIPT' && isForeignVendor)) {
    const rule = GST_LAW_KNOWLEDGE_BASE.find(r => r.id === "SEC_9_3_IMPORT_SERVICES")!;
    return {
      itcEligibility: 'RCM_APPLICABLE',
      aiComplianceNote: `RCM Applicable [Section 9(3) CGST / Section 5(3) IGST Act]: Foreign SaaS / Cloud bill from ${invoice.supplier_name || "overseas vendor"} without Indian GSTIN. Company must pay 18% IGST under RCM in GSTR-3B Table 3.1(d) and simultaneously claim 100% ITC in Table 4(A)(2).`,
      reverseChargeApplicable: true,
      matchedRule: rule
    };
  }

  // 2. Check Line Items for Blocked Credits (Section 17(5)) & RCM
  const lineItems = invoice.line_items || [];
  const fullText = (
    supplierName + " " + 
    lineItems.map(l => `${l.description} ${l.hsn_sac_code}`).join(" ")
  ).toLowerCase();

  // Test against Section 17(5) rules
  for (const rule of GST_LAW_KNOWLEDGE_BASE) {
    if (rule.category === 'BLOCKED_17_5') {
      const keywordHit = rule.keywords.some(k => fullText.includes(k));
      const sacHit = lineItems.some(l => 
        rule.sacHsnPrefixes.some(p => (l.hsn_sac_code || "").startsWith(p))
      );

      if (keywordHit || sacHit) {
        return {
          itcEligibility: 'BLOCKED_17_5',
          aiComplianceNote: `Blocked Credit [${rule.section} - ${rule.title}]: ${rule.founderAdvice}`,
          reverseChargeApplicable: false,
          matchedRule: rule
        };
      }
    }
  }

  // Test against Domestic RCM rules (Legal, Director, GTA)
  const domesticRcmRules = GST_LAW_KNOWLEDGE_BASE.filter(r => r.category === 'RCM_APPLICABLE' && r.id !== "SEC_9_3_IMPORT_SERVICES");
  for (const rule of domesticRcmRules) {
    const keywordHit = rule.keywords.some(k => fullText.includes(k));
    const sacHit = lineItems.some(l => 
      rule.sacHsnPrefixes.some(p => (l.hsn_sac_code || "").startsWith(p))
    );

    if (keywordHit || sacHit) {
      return {
        itcEligibility: 'RCM_APPLICABLE',
        aiComplianceNote: `RCM Applicable [${rule.section} - ${rule.title}]: ${rule.founderAdvice}`,
        reverseChargeApplicable: true,
        matchedRule: rule
      };
    }
  }

  // 3. Explicit reverse charge flag on invoice
  if (invoice.reverse_charge_applicable) {
    return {
      itcEligibility: 'RCM_APPLICABLE',
      aiComplianceNote: "RCM Applicable: Supplier explicitly marked Reverse Charge on invoice.",
      reverseChargeApplicable: true
    };
  }

  // 4. Default: Eligible under Section 16 CGST Act
  return {
    itcEligibility: 'ELIGIBLE',
    aiComplianceNote: "Eligible ITC [Section 16(2) CGST Act]: Standard business input tax credit. Eligible to be claimed in GSTR-3B Table 4(A)(5) subject to matching in GSTR-2B.",
    reverseChargeApplicable: false,
    matchedRule: GST_LAW_KNOWLEDGE_BASE.find(r => r.id === "SEC_16_ELIGIBLE_ITC")
  };
}
